// Pure helpers for the protected DocumentViewer. Kept framework-free (no React,
// no DOM state) so the render-decision and watermark-text logic are trivial to
// reason about and to unit-test.

import { isServerRenderedImage } from '@dataroom/shared';

/** How the viewer should render a given Content-Type. */
export type PreviewKind = 'image' | 'pdf' | 'text' | 'preview' | 'unsupported';

/**
 * Decide the render mode from the response Content-Type.
 * - `image/tiff`, `image/heic`, `image/heif` → `preview`: no browser renders
 *   these, so they are shown from the preview endpoint (pages rendered
 *   server-side, ADR-0033) — never a broken <img>
 * - `image/*`      → inline <img>
 * - `application/pdf` → the in-app pdf.js canvas viewer (ADR-0027; never the
 *   browser's PDF plugin, whose toolbar offers Download/Print)
 * - `text/plain`   → the text, read-only
 * - anything else  → a "preview not available" message (office files never get
 *   here: they are shown from the preview endpoint, ADR-0033)
 *
 * The Content-Type may carry parameters (e.g. `application/pdf; charset=…`) or
 * arrive empty, so we lower-case and match on the leading type/subtype only.
 */
export function previewKindFor(contentType: string | null | undefined): PreviewKind {
  const ct = (contentType ?? '').split(';')[0]?.trim().toLowerCase() ?? '';
  if (isServerRenderedImage(ct)) return 'preview';
  if (ct.startsWith('image/')) return 'image';
  if (ct === 'application/pdf') return 'pdf';
  if (ct === 'text/plain') return 'text';
  return 'unsupported';
}

/**
 * Build the repeating watermark label: the viewer identity plus a UTC timestamp
 * of when the document was opened. This is a DETERRENT-GRADE, presentation-layer
 * mark (it ties an on-screen capture / screenshot to a viewer + time); it is NOT
 * server-side watermarking or DRM — those burn the mark into the delivered bytes
 * and remain a tracked follow-up needing a rendering-dependency decision.
 */
export function buildWatermarkText(identity: string, now: Date = new Date()): string {
  const stamp = now.toISOString().replace('T', ' ').slice(0, 19);
  return `${identity} · ${stamp} UTC`;
}

/**
 * A CSS `url(...)` value for a tiled, diagonal watermark drawn as an inline SVG.
 * Setting it as a `background-image` on a repeating layer gives the classic
 * diagonal grid of the label across the whole viewer surface — no images to ship
 * and no runtime canvas work. The fill is intentionally faint so the document
 * stays readable underneath.
 */
export function watermarkTileDataUrl(text: string): string {
  const escaped = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  // The label is centred in its tile and the tile grows with the label (≈8.5px
  // per character at 15px, rotated 30°), so the identity is never clipped —
  // a left-anchored label lost the start of the buyer's email.
  const run = text.length * 8.5;
  const w = Math.max(340, Math.ceil(run * Math.cos(Math.PI / 6)) + 40);
  const h = Math.max(200, Math.ceil(run * Math.sin(Math.PI / 6)) + 60);
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='${w}' height='${h}'>` +
    `<text x='${w / 2}' y='${h / 2}' text-anchor='middle' dominant-baseline='middle' ` +
    `transform='rotate(-30 ${w / 2} ${h / 2})' ` +
    `font-family='ui-sans-serif, system-ui, sans-serif' font-size='15' ` +
    `fill='rgba(15,23,42,0.12)'>${escaped}</text></svg>`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
}
